import { useEffect, useState } from "react";
import { api, getUser } from "@/lib/api";

/**
 * Hook central de permissões do usuário logado.
 *
 * Retorna:
 *   - `perms`: objeto `{ chave: bool }` com todas as permissões efetivas
 *   - `canAdmin`: shortcut booleano — true se admin real OU se tem `admin_actions=true`
 *   - `ready`: false enquanto carrega
 *
 * Admin real (role=admin, is_station=false) recebe `canAdmin=true` sem chamada extra
 * — todas as chaves em `perms` viram `true`.
 *
 * Uso:
 *   const { canAdmin, ready } = useCanAdmin();
 *   {canAdmin && <button onClick={syncBling}>Sincronizar Bling</button>}
 */
export function useCanAdmin() {
  const user = getUser();
  const [state, setState] = useState({ ready: false, perms: {}, canAdmin: false });

  useEffect(() => {
    // Admin real: bypass instantâneo, sem chamada extra.
    if (user?.role === "admin" && !user?.is_station) {
      setState({ ready: true, perms: { __all__: true }, canAdmin: true });
      return;
    }
    let cancel = false;
    api.get("/permissions/me")
      .then(({ data }) => {
        if (cancel) return;
        const perms = data?.permissions || {};
        setState({
          ready: true,
          perms,
          canAdmin: !!perms.admin_actions,
        });
      })
      .catch(() => { if (!cancel) setState({ ready: true, perms: {}, canAdmin: false }); });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasPerm = (key) => {
    if (state.perms.__all__) return true;
    return !!state.perms[key];
  };

  return { ...state, hasPerm };
}
